import { useRef, useEffect, useState } from 'react';
import { useAppStore } from '@/store';
import { cn } from '@/lib/utils';
import { Paperclip, Send, Scale, Mic, MicOff, FileText, Menu, PanelRightOpen, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { format } from 'date-fns';
import { TokenGauge } from './TokenGauge';
import { PROJECT_NAME } from '@/lib/config';
import { CategoryPicker, categoryLabel } from './CategoryPicker';

function TypingDots() {
  return (
    <div className="flex gap-1 px-1 py-2">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  );
}

export function ChatInterface() {
  const {
    activeCaseId,
    cases,
    messages,
    isTyping,
    readyToGenerate,
    activeSession,
    sendUserMessage,
    uploadFiles,
    generate,
    uploadQueue,
    clearUploadQueue,
    setMobileNavOpen,
    setMobileContextOpen,
    setCaseCategory,
  } = useAppStore();

  const [input, setInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<any>(null);

  const activeCase = cases.find((c) => c.id === activeCaseId);

  // Speech recognition setup
  useEffect(() => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'uk-UA';
    rec.onresult = (e: any) => {
      let final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
      }
      if (final) setInput((p) => p + (p ? ' ' : '') + final);
    };
    rec.onend = () => setIsRecording(false);
    recognitionRef.current = rec;
  }, []);

  const toggleRecording = () => {
    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
    } else {
      recognitionRef.current?.start();
      setIsRecording(true);
    }
  };

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleSend = async () => {
    if (!input.trim() || isTyping) return;
    const text = input.trim();
    setInput('');
    await sendUserMessage(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = '';
    await uploadFiles(files);
  };

  if (!activeCaseId) {
    return (
      <div className="flex-1 flex flex-col bg-[var(--t-bg)]">
        <header className="h-14 border-b border-white/10 px-4 flex items-center gap-3 xl:hidden">
          <button
            onClick={() => setMobileNavOpen(true)}
            className="p-2 -ml-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-colors"
            title="Меню"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="text-sm font-semibold text-white/80">{PROJECT_NAME}</span>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <Scale className="h-14 w-14 mb-5 text-white/10" />
          <h2 className="text-lg font-semibold text-white/40">{PROJECT_NAME} — виберіть справу або створіть нову</h2>
          <p className="text-sm text-white/20 mt-2 max-w-xs">
            <span className="hidden xl:inline">Натисніть «Нова справа» на бічній панелі, щоб розпочати</span>
            <span className="xl:hidden">Відкрийте меню та натисніть «Нова справа», щоб розпочати</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-[var(--t-bg)] relative min-w-0 min-h-0">
      {/* Header */}
      <header className="h-14 border-b border-white/10 px-3 lg:px-6 flex items-center justify-between sticky top-0 z-10 bg-[var(--t-bg)] backdrop-blur-md shrink-0 gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <button
            onClick={() => setMobileNavOpen(true)}
            className="xl:hidden p-2 -ml-1 rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-colors shrink-0"
            title="Меню"
          >
            <Menu className="h-5 w-5" />
          </button>
          <h2 className="text-sm font-semibold text-white truncate">
            {activeCase?.title ?? 'Нова справа'}
          </h2>
          {activeSession?.category && (
            <span className="px-2 py-0.5 rounded bg-blue-600/20 border border-blue-500/30 text-[10px] text-blue-300 shrink-0 hidden sm:inline">
              {categoryLabel(activeSession.category)}
            </span>
          )}
          {activeCase?.state && (
            <span className="px-2 py-0.5 rounded bg-white/5 border border-white/10 text-[10px] text-white/50 uppercase shrink-0 hidden sm:inline">
              {activeCase.state}
            </span>
          )}
        </div>
        <button
          onClick={() => setMobileContextOpen(true)}
          className="xl:hidden p-2 -mr-1 rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-colors shrink-0"
          title="Контекст"
        >
          <PanelRightOpen className="h-5 w-5" />
        </button>
      </header>

      {/* Category picker — shown before first message if no category set */}
      {messages.length === 0 && !activeSession?.category ? (
        <CategoryPicker
          loading={categoryLoading}
          onSelect={async (cat) => {
            setCategoryLoading(true);
            try {
              await setCaseCategory(cat);
            } finally {
              setCategoryLoading(false);
            }
          }}
        />
      ) : (
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 sm:px-4 lg:px-10 py-4 lg:py-6"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 280px)' }}
      >
        <div className="max-w-3xl mx-auto space-y-6">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={cn('flex gap-3', msg.role === 'user' ? 'justify-end' : 'justify-start')}
            >
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-lg bg-blue-600 flex-shrink-0 flex items-center justify-center mt-0.5">
                  <Scale className="h-4 w-4 text-white" />
                </div>
              )}
              <div
                className={cn(
                  'group relative max-w-[85%] rounded-2xl px-3.5 sm:px-5 py-2.5 sm:py-3.5 text-sm leading-relaxed',
                  msg.role === 'user'
                    ? 'bg-white/10 text-white/90 rounded-tr-sm'
                    : 'bg-white/5 border border-white/10 text-white/80 rounded-tl-sm markdown-body'
                )}
              >
                {msg.role === 'assistant' ? (
                  msg.content
                    ? <ReactMarkdown
                        components={{
                          a: ({ node, ...props }) => (
                            <a
                              {...props}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-400 hover:text-blue-300 underline-offset-2 hover:underline"
                            />
                          ),
                          hr: () => <hr className="my-3 border-white/10" />,
                        }}
                      >{msg.content}</ReactMarkdown>
                    : <TypingDots />
                ) : (
                  <div className="whitespace-pre-wrap">{msg.content}</div>
                )}
                <span
                  className={cn(
                    'absolute text-[10px] text-white/20 opacity-0 group-hover:opacity-100 transition-opacity bottom-1',
                    msg.role === 'user' ? '-left-10' : '-right-10'
                  )}
                >
                  {format(new Date(msg.createdAt), 'HH:mm')}
                </span>
              </div>
              {msg.role === 'user' && (
                <div className="w-8 h-8 rounded-lg bg-white/10 flex-shrink-0 flex items-center justify-center text-xs text-white/60 mt-0.5">
                  Ви
                </div>
              )}
            </div>
          ))}

          {isTyping && (() => {
            const last = messages[messages.length - 1];
            // If the last bubble is the streaming assistant placeholder, the dots already live inside it.
            if (last && last.role === 'assistant') return null;
            return (
              <div className="flex gap-3 justify-start">
                <div className="w-8 h-8 rounded-lg bg-blue-600 flex-shrink-0 flex items-center justify-center mt-0.5">
                  <Scale className="h-4 w-4 text-white" />
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl rounded-tl-sm px-5 py-3">
                  <TypingDots />
                </div>
              </div>
            );
          })()}
        </div>
      </div>
      )}

      {/* Generate button — sits above the input area, never overlaps it */}
      {messages.length > 1 && (
        <div
          className="absolute left-0 right-0 flex justify-center px-3 sm:px-4 z-10 pointer-events-none"
          style={{ bottom: 'calc(env(safe-area-inset-bottom) + 190px)' }}
        >
          <button
            onClick={generate}
            disabled={isTyping}
            className={cn(
              'pointer-events-auto flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all disabled:opacity-50',
              readyToGenerate
                ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-900/40 px-6 py-3 font-semibold'
                : 'bg-white/10 hover:bg-white/15 border border-white/15 text-white/80 backdrop-blur-md text-sm'
            )}
            title={readyToGenerate ? 'Бот вважає, що зібрав усі дані' : 'Згенерувати з тим, що вже зібрано'}
          >
            <FileText className="h-4 w-4" />
            {readyToGenerate ? 'Згенерувати документ' : 'Згенерувати зараз'}
          </button>
        </div>
      )}

      {/* Upload queue indicator — above the generate button */}
      {uploadQueue.length > 0 && (
        <div
          className="absolute left-0 right-0 flex justify-center px-3 sm:px-4 z-20 pointer-events-none"
          style={{ bottom: 'calc(env(safe-area-inset-bottom) + 250px)' }}
        >
          <div className="pointer-events-auto relative bg-[var(--t-panel)] backdrop-blur-md border border-white/10 rounded-xl shadow-lg shadow-black/40 px-3 py-2 pr-8 max-w-md w-full max-h-40 sm:max-h-48 overflow-y-auto">
            <button
              type="button"
              onClick={clearUploadQueue}
              title="Закрити"
              aria-label="Закрити вікно завантаження"
              className="absolute top-1.5 right-1.5 p-1 rounded-md text-white/40 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5">
              Завантаження документів ({uploadQueue.filter((i) => i.status === 'done').length}/{uploadQueue.length})
            </div>
            <div className="space-y-1">
              {uploadQueue.map((item) => {
                const stepLabel: Record<string, string> = {
                  storing: 'Зберігаю в сховище…',
                  extracting: 'Розпізнаю текст…',
                  summarizing: 'Складаю опис…',
                  extracting_fields: 'Виділяю поля…',
                  saving: 'Зберігаю в БД…',
                };
                return (
                  <div key={item.id} className="flex items-center gap-2 text-xs">
                    <span className="w-4 h-4 shrink-0 flex items-center justify-center">
                      {item.status === 'queued' && <span className="w-1.5 h-1.5 rounded-full bg-white/30" />}
                      {item.status === 'uploading' && <span className="w-3 h-3 rounded-full border-2 border-blue-500/40 border-t-blue-500 animate-spin" />}
                      {item.status === 'done' && <span className="text-emerald-400 leading-none">✓</span>}
                      {item.status === 'error' && <span className="text-red-400 leading-none">✕</span>}
                    </span>
                    <span className="text-white/70 truncate flex-1">{item.filename}</span>
                    {item.status === 'uploading' && item.step && (
                      <span className="text-blue-300/80 text-[10px] truncate max-w-[180px]">{stepLabel[item.step] || item.step}</span>
                    )}
                    {item.summary && item.status === 'done' && (
                      <span className="text-white/40 text-[10px] truncate max-w-[180px]" title={item.summary}>{item.summary}</span>
                    )}
                    {item.error && (
                      <span className="text-red-400/70 text-[10px] truncate max-w-[180px]" title={item.error}>{item.error}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Input area — pinned to viewport bottom, with extra clearance for mobile browser chrome */}
      <div
        className="chat-input-fade pointer-events-none absolute left-0 right-0 bg-gradient-to-t from-[var(--t-bg)] to-transparent pt-6 lg:pt-8 px-3 sm:px-4 lg:px-10"
        style={{
          bottom: 0,
          paddingBottom: 'max(4rem, calc(env(safe-area-inset-bottom) + 2rem))',
        }}
      >
        <div className="pointer-events-auto max-w-3xl mx-auto">
          <div className="flex items-end gap-3">
            <TokenGauge />
            <div className="flex-1 relative bg-white/5 border border-white/10 rounded-2xl focus-within:border-blue-600/50 focus-within:ring-1 focus-within:ring-blue-600/30 transition-all">
              <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Поставте запитання або опишіть ситуацію..."
              rows={1}
              className="w-full bg-transparent px-5 py-2.5 pr-[120px] text-sm text-white placeholder:text-white/30 focus:outline-none resize-none min-h-[50px] max-h-[50px] overflow-y-auto leading-snug"
              style={{ height: '50px' }}
              onInput={(e) => {
                const t = e.target as HTMLTextAreaElement;
                t.style.height = '50px';
              }}
            />
            <div className="absolute right-3 bottom-3 flex items-center gap-1">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-2 text-white/30 hover:text-white transition-colors"
                title="Завантажити документ"
              >
                <Paperclip className="h-5 w-5" />
              </button>
              <button
                onClick={toggleRecording}
                className={cn(
                  'p-2 transition-colors',
                  isRecording ? 'text-red-400 animate-pulse' : 'text-white/30 hover:text-white'
                )}
                title="Голосове введення"
              >
                {isRecording ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
              </button>
              <button
                onClick={handleSend}
                disabled={!input.trim() || isTyping}
                className="p-2 bg-blue-600 rounded-xl hover:bg-blue-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-white ml-1"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            </div>
          </div>
          <p className="text-center text-[10px] text-white/15 mt-2 uppercase tracking-wider">
            AI може помилятися. Завжди перевіряйте юридично значущу інформацію.
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf,.docx,.doc,.md,.markdown,.txt,.csv,.html,.htm,.json,.xml"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
    </div>
  );
}
